import {
  ArrowRight,
  BookOpenCheck,
  BriefcaseBusiness,
  FileText,
  GraduationCap,
  LineChart,
  ListChecks,
  MessagesSquare,
  Sparkles,
  Trophy,
  Wrench,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getStudentGrowthProfile } from '../api.js'
import { useAuth } from '../auth-context.js'

const sectionPages = {
  student: {
    eyebrow: '学生成长',
    title: '学生成长入口',
    description: '围绕目标、学习路线、项目实践、简历面试和成长记录，把零散功能整理成可执行的成长路径。',
    cards: [
      { title: '我的成长', description: '查看当前目标、阶段、今日任务和最近成长记录。', to: '/growth', icon: ListChecks, primary: true },
      { title: 'AI伙伴', description: '通过对话认识你，记录成长记忆，并给出长期建议。', to: '/ai-partner', icon: Sparkles, primary: true },
      { title: '项目成长计划', description: '找到适合你的项目，从计划推进到可写进简历的成果。', to: '/projects/recommendations', icon: Sparkles, primary: true },
      { title: '探索方向', description: '整理专业方向、能力要求、学习路线和准备清单。', to: '/explore', icon: GraduationCap, primary: true },
      { title: '就业求职', description: '解析岗位要求、匹配简历并生成面试准备内容。', to: '/career-assistant', icon: BriefcaseBusiness },
      { title: '简历优化', description: '上传简历，获得评分、问题、建议和可复制优化文案。', to: '/more/resume', icon: FileText },
      { title: '面试准备', description: '准备自我介绍、项目表达、常见问题和模拟面试。', to: '/more/interview', icon: MessagesSquare },
      { title: '竞赛项目', description: '按专业、年级和目标筛选适合参加的竞赛。', to: '/more/competitions', icon: Trophy },
      { title: '证书规划', description: '判断证书优先级，并生成备考任务。', to: '/more/certificates', icon: BookOpenCheck },
    ],
    blocks: [
      {
        title: '成长资产',
        items: [
          { title: '在线实训中心', to: '/learning', description: '完成练习并获得反馈。' },
          { title: '项目成长工厂', to: '/project-workspace', description: '推进项目任务并提交成果。' },
          { title: '成长资产库', to: '/growth/assets', description: '查看技能、项目、学习记录和求职材料。' },
        ],
      },
    ],
  },
  general: {
    eyebrow: '大众效率',
    title: '大众效率入口',
    description: '面向普通用户的 AI 工具、内容创作、日常效率、简历求职和学习提升入口。',
    cards: [
      { title: 'AI工具', description: '发现写作、办公、图片、视频和学习类工具。', to: '/tools', icon: Wrench, primary: true },
      { title: '简历求职', description: '复用简历优化能力，整理投递材料。', to: '/more/resume', icon: FileText },
      { title: '学习提升', description: '规划学习和资料整理。', to: '/general/learning', icon: BookOpenCheck },
    ],
  },
  professional: {
    eyebrow: '专业进阶',
    title: '专业进阶入口',
    description: '按专业和就业方向进入更具体的能力提升、工具选择、项目展示和面试准备路径。',
    cards: [
      { title: '开发', description: '查看项目分析、开发者工具和榜单入口。', to: '/developer', icon: LineChart, primary: true },
      { title: '运营', description: '查看运营方向路线、作品准备和面试重点。', to: '/explore?direction=运营', icon: Sparkles },
      { title: '设计传媒', description: '查看作品集、工具和求职准备。', to: '/explore?direction=设计传媒', icon: Wrench },
      { title: '财会商科', description: '查看技能、证书和简历重点。', to: '/explore?direction=财会商科', icon: BriefcaseBusiness },
      { title: '教育文职', description: '查看能力、材料和面试准备。', to: '/explore?direction=教育文职', icon: GraduationCap },
    ],
  },
}

function DirectionCard({ item }) {
  const Icon = item.icon
  return (
    <article className={`audience-card ${item.primary ? 'is-primary' : ''}`}>
      <div>
        <span className="audience-panel-icon"><Icon size={20} /></span>
        <h3>{item.title}</h3>
        <p>{item.description}</p>
      </div>
      <Link className={item.primary ? 'primary-button' : 'secondary-button'} to={item.to}>
        进入
        <ArrowRight size={15} />
      </Link>
    </article>
  )
}

function LinkBlock({ block }) {
  return (
    <section className="audience-panel">
      <header>
        <span className="audience-panel-icon"><BriefcaseBusiness size={22} /></span>
        <div>
          <span className="eyebrow">重点方向</span>
          <h2>{block.title}</h2>
        </div>
      </header>
      <div className="audience-card-grid">
        {block.items.map((item) => (
          <article className="audience-card" key={item.title}>
            <div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
            <Link className="secondary-button" to={item.to}>
              进入 <ArrowRight size={15} />
            </Link>
          </article>
        ))}
      </div>
    </section>
  )
}

export function AudienceSectionPage({ type }) {
  const page = sectionPages[type] || sectionPages.student
  const auth = useAuth()
  const [profileState, setProfileState] = useState({ loading: false, hasProfile: true })

  useEffect(() => {
    if (type !== 'student' || auth?.loading || !auth?.user) return undefined
    const controller = new AbortController()
    setProfileState({ loading: true, hasProfile: true })
    getStudentGrowthProfile(controller.signal)
      .then((payload) => setProfileState({ loading: false, hasProfile: Boolean(payload?.hasProfile || payload?.profile) }))
      .catch(() => setProfileState({ loading: false, hasProfile: true }))
    return () => controller.abort()
  }, [auth?.loading, auth?.user, type])

  return (
    <main className="page-container audience-home-page">
      <section className="audience-hero">
        <div className="audience-hero-copy">
          <span className="eyebrow">{page.eyebrow}</span>
          <h1>{page.title}</h1>
          <p>{page.description}</p>
        </div>
      </section>

      {type === 'student' && auth?.user && !profileState.loading && !profileState.hasProfile ? (
        <section className="audience-profile-guide">
          <div>
            <span className="eyebrow">AI伙伴</span>
            <h2>先让系统认识你，再生成更贴近你的成长路径</h2>
            <p>AI伙伴会通过对话了解你的专业、年级、兴趣、目标和困难，并沉淀成长记忆。</p>
          </div>
          <Link className="primary-button" to="/ai-partner">
            开始对话 <ArrowRight size={15} />
          </Link>
        </section>
      ) : null}

      {type === 'student' && auth?.user && !profileState.loading && profileState.hasProfile ? (
        <section className="audience-profile-guide">
          <div>
            <span className="eyebrow">我的成长画像</span>
            <h2>已建立成长画像，继续推进今天的学习和项目</h2>
            <p>查看学习路线、今日任务和项目实践，也可以随时更新成长画像。</p>
          </div>
          <div className="audience-profile-actions">
            <Link className="primary-button" to="/growth">
              进入我的成长 <ArrowRight size={15} />
            </Link>
            <Link className="secondary-button" to="/ai-partner">更新画像</Link>
            <Link className="secondary-button" to="/growth/assets">成长资产库</Link>
          </div>
        </section>
      ) : null}

      <section className="audience-panel">
        <header>
          <span className="audience-panel-icon"><GraduationCap size={22} /></span>
          <div>
            <span className="eyebrow">核心方向</span>
            <h2>{page.title}</h2>
          </div>
        </header>
        <div className="audience-card-grid">
          {page.cards.map((item) => <DirectionCard item={item} key={item.title} />)}
        </div>
      </section>

      {page.blocks?.map((block) => <LinkBlock block={block} key={block.title} />)}
    </main>
  )
}
